import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, ArrowRight } from 'lucide-react';

import OptimizedImage from '@/components/media/OptimizedImage';
import { pathFor } from '@/data/routes';
import { photoForArticle } from '@/data/photos';
import { usePrefetchOnHover } from '@/hooks/usePrefetchOnHover';
import { getCategoryBySlug } from '@/data/categories';

const COVER_BY_CATEGORY = {
  'visa-residence': '/covers/visa-residence.svg',
  'banque-argent': '/covers/banque-argent.svg',
  'assurance': '/covers/assurance.svg',
  'animaux-chiens': '/covers/animaux-chiens.svg',
  'vols': '/covers/vols.svg',
  'espagnol': '/covers/espagnol.svg',
  'vie-quotidienne': '/covers/vie-quotidienne.svg',
  'immobilier': '/covers/immobilier.svg',
};
function coverFor(category) {
  return COVER_BY_CATEGORY[category] ?? '/og-default.svg';
}

/**
 * Article card that expands to a near-fullscreen preview on click,
 * inspired by the Framer cards-expand demo. Uses Framer Motion's `layoutId`
 * for a shared-element transition (one DOM node, scaled+morphed between
 * grid and overlay states).
 *
 * The preview shows the article's hero image, headline, excerpt, key
 * facts (read time, category, date), and a CTA to read the full article.
 * Click outside / Esc / X closes.
 *
 * Behaviour with `prefers-reduced-motion`: instant open/close (no spring).
 *
 * @param {object} props
 * @param {object} props.article  The article frontmatter (title, slug, excerpt, category, image, readTime, date, author)
 * @param {string} [props.lang='fr']
 *
 * @example
 *   <ExpandableArticleCard article={frontmatter} lang={lang} />
 */
// Libellé du teaser FAQ. Traduire AVANT de designer (charte V6).
const TEASER_LABEL = {
  fr: 'Une question à laquelle il répond',
  en: 'One question it answers',
  es: 'Una pregunta que responde',
};

export default function ExpandableArticleCard({ article, lang = 'fr' }) {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  // 1re paire FAQ de l'article, si elle existe. Aucun coût : le frontmatter
  // est déjà chargé (glob eager de utils/articles.js).
  const teaser = Array.isArray(article.faq) && article.faq[0]?.question && article.faq[0]?.answer
    ? article.faq[0]
    : null;
  const id = `card-${article.slug}`;

  const cover = article.featuredImage || article.image || photoForArticle?.(article.slug) || coverFor(article.category);
  const articleHref = pathFor('article', lang, { category: article.category, slug: article.slug });
  const prefetch = usePrefetchOnHover(() => import('@/pages/Article'));

  // Close on Esc + lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  const transition = reduce ? { duration: 0 } : { type: 'spring', stiffness: 280, damping: 30 };

  return (
    <>
      {/* Grid card — clickable, transitions to overlay on click. Le hover
          déclenche aussi le prefetch du chunk Article.jsx car beaucoup
          d'utilisateurs survolent la card → cliquent "Lire l'article
          complet" 200 ms plus tard. Le chunk est déjà en cache quand
          ils cliquent. */}
      <motion.button
        type="button"
        layoutId={open ? undefined : id}
        onClick={() => setOpen(true)}
        className="card card-hoverable group relative block w-full text-left"
        aria-expanded={open}
        aria-label={`Aperçu : ${article.title}`}
        {...prefetch}
      >
        <motion.div
          layoutId={open ? undefined : `${id}-image`}
          className="aspect-video w-full overflow-hidden"
        >
          <OptimizedImage
            src={cover}
            alt={article.featuredImageAlt || ''}
            width={640}
            height={360}
            cdnProxy
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </motion.div>
        <div className="p-5">
          <p className="v4-eyebrow text-bordeaux-700 dark:text-bordeaux-400">
            {/* 2026-05-17 — same fix as ArticleCard : resolve translated label
                from category slug + active lang. Previously leaked "VIE-
                QUOTIDIENNE" / "BANQUE-ARGENT" on Home cards in /en/ + /es/. */}
            {article.categoryLabel
              ?? getCategoryBySlug(article.category)?.label?.[lang]
              ?? getCategoryBySlug(article.category)?.label?.fr
              ?? article.category}
          </p>
          <motion.h3
            layoutId={open ? undefined : `${id}-title`}
            className="mt-2 line-clamp-2 font-serif text-xl italic font-semibold text-navy-900 dark:text-white"
          >
            {article.title}
          </motion.h3>
          {article.excerpt && (
            <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-700 dark:text-white/70">
              {article.excerpt}
            </p>
          )}
        </div>
      </motion.button>

      {/* Full-screen overlay — same layoutId so the image / title smoothly
          flow from card position to the overlay layout. */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-50 bg-navy-900/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={transition}
              aria-hidden="true"
            />
            <motion.div
              layoutId={id}
              className="fixed inset-x-2 inset-y-4 z-50 mx-auto flex max-h-[92svh] max-w-3xl flex-col overflow-hidden rounded-card bg-cream-50 shadow-2xl md:inset-x-auto md:inset-y-12 md:left-1/2 md:w-full md:max-w-3xl md:-translate-x-1/2 dark:bg-zinc-900 dark:ring-1 dark:ring-white/10"
              transition={transition}
              role="dialog"
              aria-modal="true"
              aria-label={article.title}
            >
              {/* Close button */}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-cream-50/95 text-navy-900 shadow-md hover:bg-bordeaux-600 hover:text-cream-50 transition-colors dark:bg-zinc-900/95 dark:text-white dark:hover:bg-bordeaux-500"
                aria-label={lang === 'en' ? 'Close preview' : lang === 'es' ? 'Cerrar vista previa' : 'Fermer aperçu'}
              >
                <X size={18} aria-hidden="true" />
              </button>

              <motion.div
                layoutId={`${id}-image`}
                className="aspect-[16/9] max-h-[32vh] w-full shrink-0 overflow-hidden"
              >
                <OptimizedImage
                  src={cover}
                  alt={article.featuredImageAlt || ''}
                  width={1280}
                  height={720}
                  className="h-full w-full object-cover"
                  priority
                />
              </motion.div>

              {/* Contenu — scroll interne UNIQUEMENT si l'extrait est très
                  long. Image plafonnée (32vh) + titre/extrait compacts →
                  tout tient dans le viewport pour la plupart des articles. */}
              <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-5 md:px-10 md:pt-6">
                <p className="v4-eyebrow text-bordeaux-700 dark:text-bordeaux-400">
                  {article.categoryLabel
                    ?? getCategoryBySlug(article.category)?.label?.[lang]
                    ?? getCategoryBySlug(article.category)?.label?.fr
                    ?? article.category}
                </p>
                <motion.h2
                  layoutId={`${id}-title`}
                  className="mt-1.5 font-serif text-2xl italic font-bold text-navy-900 md:text-3xl dark:text-white"
                >
                  {article.title}
                </motion.h2>

                {article.excerpt && (
                  <p className="mt-3 line-clamp-4 text-base leading-relaxed text-gray-700 dark:text-white/70">
                    {article.excerpt}
                  </p>
                )}

                {/* Une question à laquelle cet article répond, tirée de son
                    propre frontmatter `faq:`. Montre ce que le lecteur va
                    réellement obtenir, avec ses mots à lui plutôt qu'un résumé.
                    Posé dans le panneau OUVERT, pas au survol : la carte
                    s'ouvre au clic, et un aperçu au survol serait invisible au
                    tactile comme au clavier. */}
                {teaser && (
                  <div className="mt-4 border-l-2 border-gold-500 pl-3">
                    <p className="v4-label-mono text-gray-700 dark:text-white/50">{TEASER_LABEL[lang] ?? TEASER_LABEL.fr}</p>
                    <p className="mt-1 font-serif text-[15px] text-navy-900 dark:text-white">{teaser.question}</p>
                    <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-gray-700 dark:text-white/70">
                      {teaser.answer}
                    </p>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 dark:text-white/70">
                  {article.readTime && (
                    <span>
                      ⏱{' '}
                      {lang === 'en'
                        ? `${article.readTime} min read`
                        : lang === 'es'
                          ? `${article.readTime} min de lectura`
                          : `${article.readTime} min de lecture`}
                    </span>
                  )}
                  {article.author && <span>✍️ {article.author}</span>}
                  {article.date && <span>📅 {article.date}</span>}
                </div>
              </div>

              {/* Footer CTA — TOUJOURS visible, hors zone de scroll (fix Jade
                  2026-06-14 : le bouton « Lire l'article » tombait sous la
                  ligne de flottaison, il fallait scroller pour l'atteindre). */}
              <div className="shrink-0 border-t border-navy-900/10 bg-cream-50 px-6 py-4 md:px-10 dark:border-white/10 dark:bg-zinc-900">
                <Link
                  to={articleHref}
                  className="btn-primary flex w-full items-center justify-center gap-2 sm:inline-flex sm:w-auto"
                  onClick={() => setOpen(false)}
                  {...prefetch}
                >
                  {lang === 'en'
                    ? 'Read the full article'
                    : lang === 'es'
                      ? 'Leer el artículo completo'
                      : "Lire l'article complet"}
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
